import type { ReactNode } from "react";
import { useState } from "react";
import { Pressable, ScrollView, View } from "react-native";

import { Divider, Icon, Text } from "react-native-paper";

import { useAppTheme } from "@hooks/useAppTheme";

import { useActiveScenario } from "../hooks/useActiveScenario";
import { ScenarioMarkdown } from "./ScenarioMarkdown";
import { ScenarioSummary } from "./ScenarioSummary";

const Section = ({
	label,
	children,
}: { label: string; children: ReactNode }) => {
	const [open, setOpen] = useState(true);
	const theme = useAppTheme();
	return (
		<View>
			<Pressable
				onPress={() => setOpen((o) => !o)}
				accessibilityRole="button"
				accessibilityState={{ expanded: open }}
				style={{
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: open ? 8 : 0,
				}}
			>
				<Text
					variant="labelSmall"
					style={{
						color: theme.colors.onSurfaceVariant,
						textTransform: "uppercase",
						letterSpacing: 1,
					}}
				>
					{label}
				</Text>
				<Icon
					source={open ? "chevron-down" : "chevron-right"}
					size={16}
					color={theme.colors.onSurfaceVariant}
				/>
			</Pressable>
			{open ? children : null}
		</View>
	);
};

const BulletList = ({ items }: { items: string[] }) => {
	const theme = useAppTheme();
	return (
		<View style={{ gap: 6 }}>
			{items.map((item) => (
				<View key={item} style={{ flexDirection: "row", gap: 8 }}>
					<Text style={{ color: theme.colors.onSurfaceVariant }}>•</Text>
					<View style={{ flex: 1 }}>
						<ScenarioMarkdown text={item} />
					</View>
				</View>
			))}
		</View>
	);
};

export const ContextPanel = () => {
	const { scenario: s } = useActiveScenario();
	const theme = useAppTheme();

	return (
		<ScrollView
			style={{ flex: 1, backgroundColor: theme.colors.surface }}
			contentContainerStyle={{ padding: 16 }}
		>
			<Section label="Persona">
				<Text variant="titleSmall" style={{ color: theme.colors.onSurface }}>
					{s.persona.name}
				</Text>
				<Text
					variant="bodySmall"
					style={{ color: theme.colors.onSurfaceVariant, marginBottom: 6 }}
				>
					{s.persona.role}
				</Text>
				{s.persona.description && (
					<ScenarioMarkdown text={s.persona.description} />
				)}
			</Section>

			<Divider style={{ marginVertical: 16 }} />

			<Section label="Scenario">
				<ScenarioSummary
					paragraphs={s.scenarioSummary}
					media={s.scenarioMedia}
					imageMaxWidth={280}
				/>
			</Section>

			<Divider style={{ marginVertical: 16 }} />

			<Section label="Objectives">
				<BulletList items={s.learningObjectives} />
			</Section>
		</ScrollView>
	);
};
